// src/pages/TacheEdit.js - AVEC GESTION DES ERREURS D'ABONNEMENT
import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const TacheEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [formData, setFormData] = useState({
    titre_tache: "",
    description_tache: "",
    date_debut: "",
    date_fin: "",
    priorite: "moyenne",
    id_agent: ""
  });
  
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [originalData, setOriginalData] = useState(null);

  // Couleurs de la charte graphique
  const COLORS = {
    primary: '#0B3C5D',
    secondary: '#1E88C8',
    accent: '#4FC3F7',
    darkText: '#2E2E2E',
    lightBg: '#F5F7FA',
    white: '#FFFFFF',
    danger: '#f44336',
    success: '#4caf50',
    warning: '#ff9800',
    info: '#2196f3'
  };

  // Récupérer les infos utilisateur
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        const normalizedData = {
          ...userData,
          profile: userData.profile || userData.poste || 0,
          societe_id: userData.societe_id || 0
        };
        setUserInfo(normalizedData);
      } catch (error) {
        navigate("/login");
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  // Charger les détails de la tâche
  useEffect(() => {
    const fetchTacheDetails = async () => {
      if (!id || !userInfo) return;
      
      setLoading(true);
      setError("");
      
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Session expirée");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }
      
      try {
        // Charger la tâche
        const response = await axios.get(
          `${API_BASE_URL}/taches/get.php?id=${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            }
          }
        );
        
        console.log("Réponse API tâche:", response.data);
        
        if (response.data?.success) {
          const tache = response.data.tache;
          console.log("Tâche chargée:", tache);
          setOriginalData(tache);
          
          // Formater les dates pour les inputs datetime-local — voir le
          // correctif équivalent dans handleReset ci-dessous : éviter tout
          // passage par toISOString() qui décale l'heure vers UTC.
          const formatDateForInput = (dateString) => {
            if (!dateString || dateString === "0000-00-00" || dateString === "0000-00-00 00:00:00") {
              return "";
            }
            return dateString.replace(' ', 'T').slice(0, 16);
          };
          
          setFormData({
            titre_tache: tache.titre_tache || "",
            description_tache: tache.description_tache || "",
            date_debut: formatDateForInput(tache.date_debut),
            date_fin: formatDateForInput(tache.date_fin),
            priorite: tache.priorite || "moyenne",
            id_agent: tache.id_agent ? tache.id_agent.toString() : ""
          });
          
          // Vérifier si la tâche est terminée
          if (tache.statut_tache === 'termine' || tache.statut_tache === 'terminée') {
            setError("Impossible de modifier une tâche terminée.");
          }
        } else {
          setError(response.data?.message || "Tâche non trouvée.");
        }
      } catch (err) {
        console.error("Erreur lors du chargement:", err);
        if (err.response?.status === 401) {
          setError("Session expirée. Veuillez vous reconnecter.");
          localStorage.clear();
          setTimeout(() => navigate("/login"), 2000);
        } else if (err.response?.status === 403) {
          setError("Accès interdit. Vous n'avez pas les permissions nécessaires.");
        } else if (err.response?.status === 404) {
          setError("Tâche non trouvée.");
        } else if (!err.response) {
          setError("Serveur indisponible. Vérifiez votre connexion.");
        } else {
          setError(`Erreur ${err.response.status}: ${err.response.data?.message || "Erreur technique"}`);
        }
      } finally {
        setLoading(false);
      }
    };
    
    if (userInfo) {
      fetchTacheDetails();
    }
  }, [id, userInfo, navigate]);

  // Charger la liste des agents
  useEffect(() => {
    const fetchAgents = async () => {
      if (!userInfo) return;
      
      setError("");
      
      const token = localStorage.getItem("access_token");
      if (!token) return;
      
      try {
        const response = await axios.get(
          `${API_BASE_URL}/agents/list.php`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            params: {
              limit: 1000,
              statut: 1,
              show_deleted: false
            }
          }
        );
        
        console.log("Réponse API agents:", response.data);
        
        if (response.data?.success) {
          const agentsList = response.data.agents || [];
          console.log("Agents chargés:", agentsList);
          setAgents(agentsList);
        }
      } catch (err) {
        console.error("Erreur chargement agents:", err);
      }
    };
    
    if (userInfo) {
      fetchAgents();
    }
  }, [userInfo, navigate]);

  // Gestion des changements dans le formulaire
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  // Validation du formulaire
  const validateForm = () => {
    const errors = [];
    
    if (!formData.id_agent) {
      errors.push("L'agent est obligatoire");
    }
    
    if (!formData.titre_tache.trim()) {
      errors.push("Le titre de la tâche est obligatoire");
    }
    
    if (!formData.description_tache.trim()) {
      errors.push("La description est obligatoire");
    }
    
    if (!formData.date_debut) {
      errors.push("La date de début est obligatoire");
    }
    
    if (!formData.date_fin) {
      errors.push("La date de fin est obligatoire");
    }
    
    if (formData.date_debut && formData.date_fin) {
      const startDate = new Date(formData.date_debut);
      const endDate = new Date(formData.date_fin);
      
      if (endDate <= startDate) {
        errors.push("La date de fin doit être postérieure à la date de début");
      }
    }
    
    if (formData.priorite && !['basse', 'moyenne', 'haute', 'critique'].includes(formData.priorite)) {
      errors.push("Priorité invalide");
    }
    
    setValidationErrors(errors);
    return errors.length === 0;
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSaving(true);
    setError("");
    setSuccessMsg("");
    
    try {
      const token = localStorage.getItem("access_token");
      
      // Formatage des dates pour l'API — voir le même correctif dans
      // TacheCreate.js : dateString vient d'un <input type="datetime-local">
      // déjà en heure locale, un passage par toISOString() décalait l'heure
      // envoyée au backend.
      const formatDateForAPI = (dateString) => {
        if (!dateString) return '';
        return dateString.replace('T', ' ') + ':00';
      };
      
      const tacheData = {
        id_tache: parseInt(id),
        titre_tache: formData.titre_tache.trim(),
        description_tache: formData.description_tache.trim(),
        date_debut: formatDateForAPI(formData.date_debut),
        date_fin: formatDateForAPI(formData.date_fin),
        priorite: formData.priorite,
        id_agent: parseInt(formData.id_agent)
      };
      
      console.log("Données envoyées à l'API:", tacheData);
      
      const response = await axios.post(
        `${API_BASE_URL}/taches/update.php`,
        tacheData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      
      console.log("Réponse API:", response.data);
      
      // Vérification flexible de la réussite
      if (response.data?.success === true || response.data?.success === "true" || 
          response.data?.status === "success" || response.data?.message?.includes("succès")) {
        
        const successMessage = response.data?.message || "Tâche mise à jour avec succès !";
        setSuccessMsg(successMessage);
        
        // Redirection après 1.5 secondes
        setTimeout(() => {
          navigate(`/taches/details/${id}`);
        }, 1500);
        
      } else {
        const errorMsg = response.data?.message || 
                        response.data?.error || 
                        "Erreur lors de la modification de la tâche";
        setError(errorMsg);
        
        if (response.data?.errors) {
          const validationErrors = Array.isArray(response.data.errors) 
            ? response.data.errors 
            : [response.data.errors];
          setValidationErrors(validationErrors);
        }
      }
      
    } catch (err) {
      console.error("Erreur lors de la soumission:", err);
      console.error("Données erreur:", err.response?.data);
      
      if (err.response?.status === 401) {
        setError("Session expirée. Veuillez vous reconnecter.");
        localStorage.clear();
        setTimeout(() => navigate("/login"), 2000);
        return;
      } else if (err.response?.status === 403) {
        // 🔍 Vérifier si c'est une erreur d'abonnement
        if (err.response.data?.code === 'ABONNEMENT_REQUIS') {
          // Message avec bouton de souscription
          setError(
            <div>
              <div style={{ whiteSpace: 'pre-line', marginBottom: '15px' }}>
                {err.response.data.message}
              </div>
              <div className="d-flex gap-2 justify-content-center">
                <a 
                  href="/abonnements/create"
                  className="btn fw-semibold"
                  style={{
                    backgroundColor: '#4caf50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 20px',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
                >
                  <i className="bi bi-box-arrow-up-right"></i>
                  Souscrire à un abonnement
                </a>
                <button
                  className="btn fw-semibold"
                  onClick={() => setError("")}
                  style={{
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 20px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
                >
                  Fermer
                </button>
              </div>
            </div>
          );
        } else {
          setError("Accès interdit. Vous n'avez pas les permissions nécessaires.");
        }
        return;
      } else if (err.response?.status === 400) {
        const serverError = err.response.data?.message || 
                           err.response.data?.error || 
                           "Données invalides";
        
        if (err.response.data?.errors) {
          const validationErrors = Array.isArray(err.response.data.errors) 
            ? err.response.data.errors 
            : Object.values(err.response.data.errors).flat();
          setValidationErrors(validationErrors);
        } else {
          setValidationErrors([serverError]);
        }
        return;
      } else if (!err.response) {
        setError("Serveur indisponible. Vérifiez que l'API est accessible.");
        return;
      } else if (err.response?.status === 500) {
        setError("Erreur serveur interne. Veuillez contacter l'administrateur.");
        return;
      } else {
        setError(`Erreur ${err.response.status}: ${err.response.data?.message || "Erreur technique"}`);
      }
    } finally {
      setSaving(false);
    }
  };

  // Réinitialiser le formulaire
  const handleReset = () => {
    if (originalData) {
      // dateString est une chaîne naïve "YYYY-MM-DD HH:MM:SS" renvoyée par le
      // backend (heure locale telle que saisie) : passer par
      // `new Date(...).toISOString()` la réinterprète comme heure locale puis
      // la convertit en UTC, décalant l'heure affichée dans le champ
      // datetime-local (ex: -1h en WAT/UTC+1). Un simple reformatage de chaîne
      // évite toute conversion de fuseau.
      const formatDateForInput = (dateString) => {
        if (!dateString || dateString === "0000-00-00" || dateString === "0000-00-00 00:00:00") {
          return "";
        }
        return dateString.replace(' ', 'T').slice(0, 16);
      };
      
      setFormData({
        titre_tache: originalData.titre_tache || "",
        description_tache: originalData.description_tache || "",
        date_debut: formatDateForInput(originalData.date_debut),
        date_fin: formatDateForInput(originalData.date_fin),
        priorite: originalData.priorite || "moyenne",
        id_agent: originalData.id_agent ? originalData.id_agent.toString() : ""
      });
    }
    setError("");
    setSuccessMsg("");
    setValidationErrors([]);
  };

  // Annuler et retourner
  const handleCancel = () => {
    navigate(`/taches/details/${id}`);
  };

  // Obtenir la couleur selon la priorité
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'basse': return '#4caf50';
      case 'moyenne': return '#ff9800';
      case 'haute': return '#f44336';
      case 'critique': return '#d32f2f';
      default: return COLORS.darkText;
    }
  };

  // Afficher l'écran de chargement
  if (loading) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Modifier une tâche</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/taches">Tâches</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to={`/taches/details/${id}`}>Détails</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Chargement...</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="spinner-border" style={{ color: COLORS.primary }} role="status"></div>
              <p className="mt-3" style={{ color: COLORS.darkText }}>Chargement des données de la tâche...</p>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  // Vérifier les permissions après le chargement
  if (userInfo && String(userInfo.profile).trim() !== "3") {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Modifier une tâche</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/taches">Tâches</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Modifier</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ 
                width: '80px',
                height: '80px',
                backgroundColor: '#ffebee',
                color: COLORS.danger
              }}>
                <i className="bi bi-shield-lock" style={{ fontSize: '2rem' }}></i>
              </div>
              <h5 style={{ color: COLORS.primary, fontWeight: '600' }}>Accès restreint</h5>
              <p className="text-muted">Seuls les gestionnaires peuvent modifier des tâches.</p>
              <button
                className="btn mt-3 fw-semibold"
                onClick={() => navigate("/taches")}
                style={{
                  color: COLORS.primary,
                  borderColor: COLORS.primary,
                  backgroundColor: 'transparent',
                  borderRadius: '8px',
                  padding: '10px 24px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = COLORS.primary;
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = COLORS.primary;
                }}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Retour à la liste
              </button>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

 

// Gérer les erreurs avant d'afficher le formulaire
if (error && typeof error === 'string' && (error.includes("terminée") || error.includes("non trouvée"))) {
  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Modifier une tâche</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Dashboard</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/taches">Tâches</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Modifier</li>
          </ol>
        </nav>
      </div>
      
      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <div className="alert alert-danger alert-dismissible fade show mb-4" style={{ 
              backgroundColor: '#ffebee',
              borderColor: COLORS.danger,
              color: '#c62828',
              borderRadius: '8px',
              borderLeft: '4px solid ' + COLORS.danger
            }}>
              <div className="d-flex align-items-center">
                <i className="bi bi-exclamation-triangle me-2"></i>
                <div className="flex-grow-1">
                  <strong>Erreur :</strong> {error}
                </div>
              </div>
            </div>
            
            <div className="text-center">
              <button
                className="btn fw-semibold me-2"
                onClick={() => navigate("/taches")}
                style={{
                  color: COLORS.primary,
                  borderColor: COLORS.primary,
                  backgroundColor: 'transparent',
                  borderRadius: '8px',
                  padding: '10px 24px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#E3F2FD';
                  e.target.style.borderColor = COLORS.secondary;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.borderColor = COLORS.primary;
                }}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Retour à la liste
              </button>
              
              <button
                className="btn fw-semibold"
                onClick={() => navigate(`/taches/details/${id}`)}
                style={{
                  color: COLORS.info,
                  borderColor: COLORS.info,
                  backgroundColor: 'transparent',
                  borderRadius: '8px',
                  padding: '10px 24px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#E3F2FD';
                  e.target.style.borderColor = COLORS.secondary;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.borderColor = COLORS.info;
                }}
              >
                <i className="bi bi-eye me-2"></i>
                Voir les détails
              </button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

  // Afficher le formulaire de modification
  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Modifier une tâche</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Dashboard</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/taches">Tâches</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to={`/taches/details/${id}`}>Détails #{id}</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Modification</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <h5 className="card-title" style={{ 
              color: COLORS.primary,
              borderBottom: `2px solid ${COLORS.lightBg}`,
              paddingBottom: '16px',
              marginBottom: '24px'
            }}>
              <i className="bi bi-pencil-square me-2" style={{ color: COLORS.secondary }}></i>
              Modification de la tâche #{id}
            </h5>

            {/* Messages */}
            {successMsg && (
              <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#e8f5e9',
                borderColor: COLORS.success,
                color: '#2e7d32',
                borderRadius: '8px',
                borderLeft: '4px solid ' + COLORS.success
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-check-circle me-2" style={{ fontSize: '1.2rem' }}></i>
                  <div className="flex-grow-1">
                    <strong>Succès !</strong> {successMsg}
                    <div className="mt-2">
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Chargement...</span>
                      </div>
                      <span>Redirection en cours...</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="alert alert-danger alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: '4px solid ' + COLORS.danger,
                whiteSpace: "pre-line"
              }}>
                <div className="d-flex align-items-start">
                  <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
                  <div className="flex-grow-1">
                    <strong>Erreur :</strong> {error}
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setError("")}
                  ></button>
                </div>
              </div>
            )}

            {validationErrors.length > 0 && (
              <div className="alert alert-warning alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#fff8e1',
                borderColor: COLORS.warning,
                color: '#ff8f00',
                borderRadius: '8px',
                borderLeft: '4px solid ' + COLORS.warning
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">
                    <strong>Veuillez corriger les erreurs suivantes :</strong>
                    <ul className="mb-0 mt-2">
                      {validationErrors.map((err, index) => (
                        <li key={index}>{err}</li>
                      ))}
                    </ul>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setValidationErrors([])}
                  ></button>
                </div>
              </div>
            )}

            {/* Formulaire */}
            <form onSubmit={handleSubmit}>
              {/* Informations principales */}
              <div className="card mb-4" style={{ 
                borderRadius: '8px',
                border: `1px solid ${COLORS.lightBg}`,
                boxShadow: 'none'
              }}>
                <div className="card-body">
                  <h6 className="card-title mb-4" style={{ 
                    color: COLORS.primary,
                    fontWeight: '600'
                  }}>
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                      width: '30px',
                      height: '30px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-info-circle"></i>
                    </div>
                    Informations de la tâche
                  </h6>
                  
                  {/* Agent et Titre */}
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-person-badge me-1" style={{ color: COLORS.secondary }}></i>
                          Agent assigné <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-person"></i>
                          </span>
                          <select
                            className="form-select"
                            name="id_agent"
                            value={formData.id_agent}
                            onChange={handleChange}
                            required
                            disabled={saving}
                            style={{ 
                              borderColor: '#e0e0e0',
                              borderRadius: '0 8px 8px 0',
                              color: COLORS.darkText
                            }}
                          >
                            <option value="">Sélectionner un agent</option>
                            {agents.map(agent => (
                              <option key={agent.id_agent} value={agent.id_agent}>
                                {agent.nom_agent} {agent.prenom_agent} 
                                {agent.status_agent !== 1 && " (Inactif)"}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-text" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                          <i className="bi bi-info-circle me-1"></i>
                          {agents.length} agent{agents.length > 1 ? 's' : ''} disponible{agents.length > 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-card-heading me-1" style={{ color: COLORS.secondary }}></i>
                          Titre de la tâche <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-pencil"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            name="titre_tache"
                            value={formData.titre_tache}
                            onChange={handleChange}
                            required
                            disabled={saving}
                            placeholder="Ex: Contrôle de sécurité, Rondes, Formation..."
                            style={{ 
                              borderColor: '#e0e0e0',
                              borderRadius: '0 8px 8px 0',
                              color: COLORS.darkText
                            }}
                          />
                        </div>
                        <div className="form-text" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                          <i className="bi bi-info-circle me-1"></i>
                          Titre court et descriptif (max 100 caractères)
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="row mt-3">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-card-text me-1" style={{ color: COLORS.secondary }}></i>
                          Description détaillée <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text align-items-start" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary,
                            paddingTop: '12px'
                          }}>
                            <i className="bi bi-text-paragraph"></i>
                          </span>
                          <textarea
                            className="form-control"
                            name="description_tache"
                            value={formData.description_tache}
                            onChange={handleChange}
                            required
                            disabled={saving}
                            rows="5"
                            placeholder="Décrivez précisément la tâche à réaliser, les consignes spécifiques, les procédures à suivre, les équipements à utiliser, etc."
                            style={{ 
                              borderColor: '#e0e0e0',
                              borderRadius: '0 8px 8px 0',
                              color: COLORS.darkText,
                              resize: 'vertical'
                            }}
                          />
                        </div>
                        <div className="form-text" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                          <i className="bi bi-info-circle me-1"></i>
                          Soyez le plus précis possible pour éviter les ambiguïtés
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dates et Priorité */}
              <div className="card mb-4" style={{ 
                borderRadius: '8px',
                border: `1px solid ${COLORS.lightBg}`,
                boxShadow: 'none'
              }}>
                <div className="card-body">
                  <h6 className="card-title mb-4" style={{ 
                    color: COLORS.primary,
                    fontWeight: '600'
                  }}>
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                      width: '30px',
                      height: '30px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-calendar-check"></i>
                    </div>
                    Planning et Priorité
                  </h6>
                  
                  <div className="row">
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-calendar-plus me-1" style={{ color: COLORS.secondary }}></i>
                          Date de début <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-clock"></i>
                          </span>
                          <input
                            type="datetime-local"
                            className="form-control"
                            name="date_debut"
                            value={formData.date_debut}
                            onChange={handleChange}
                            required
                            disabled={saving}
                            style={{ 
                              borderColor: '#e0e0e0',
                              borderRadius: '0 8px 8px 0',
                              color: COLORS.darkText
                            }}
                          />
                        </div>
                        <div className="form-text" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                          <i className="bi bi-info-circle me-1"></i>
                          Date et heure de début prévues
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-calendar-minus me-1" style={{ color: COLORS.secondary }}></i>
                          Date de fin <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-clock-history"></i>
                          </span>
                          <input
                            type="datetime-local"
                            className="form-control"
                            name="date_fin"
                            value={formData.date_fin}
                            onChange={handleChange}
                            required
                            disabled={saving}
                            min={formData.date_debut}
                            style={{ 
                              borderColor: '#e0e0e0',
                              borderRadius: '0 8px 8px 0',
                              color: COLORS.darkText
                            }}
                          />
                        </div>
                        <div className="form-text" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                          <i className="bi bi-info-circle me-1"></i>
                          Date et heure de fin prévues
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-exclamation-triangle me-1" style={{ color: COLORS.secondary }}></i>
                          Priorité
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-flag"></i>
                          </span>
                          <select
                            className="form-select"
                            name="priorite"
                            value={formData.priorite}
                            onChange={handleChange}
                            disabled={saving}
                            style={{ 
                              borderColor: '#e0e0e0',
                              borderRadius: '0 8px 8px 0',
                              color: COLORS.darkText
                            }}
                          >
                            <option value="basse">Basse</option>
                            <option value="moyenne">Moyenne</option>
                            <option value="haute">Haute</option>
                            <option value="critique">Critique</option>
                          </select>
                        </div>
                        <div className="form-text" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                          <i className="bi bi-info-circle me-1"></i>
                          Niveau d'urgence de la tâche
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Indicateur de durée */}
                  {formData.date_debut && formData.date_fin && (
                    <div className="mt-3">
                      <div className="alert" style={{ 
                        backgroundColor: '#E3F2FD',
                        borderColor: COLORS.accent,
                        color: COLORS.primary,
                        borderRadius: '8px'
                      }}>
                        <div className="d-flex align-items-center">
                          <i className="bi bi-clock me-2" style={{ color: COLORS.secondary }}></i>
                          <div>
                            <strong>Durée estimée : </strong>
                            {(() => {
                              const start = new Date(formData.date_debut);
                              const end = new Date(formData.date_fin);
                              const diffMs = end - start;
                              const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                              const diffDays = Math.floor(diffHours / 24);
                              const remainingHours = diffHours % 24;
                              
                              let durationText = '';
                              if (diffDays > 0) {
                                durationText += `${diffDays} jour${diffDays > 1 ? 's' : ''} `;
                              }
                              if (remainingHours > 0) {
                                durationText += `${remainingHours} heure${remainingHours > 1 ? 's' : ''}`;
                              }
                              
                              return durationText || 'Moins d\'une heure';
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Récapitulatif */}
              <div className="card mb-4" style={{ 
                borderRadius: '8px',
                border: `1px solid ${COLORS.lightBg}`,
                boxShadow: 'none'
              }}>
                <div className="card-body">
                  <h6 className="card-title mb-4" style={{ 
                    color: COLORS.primary,
                    fontWeight: '600'
                  }}>
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                      width: '30px',
                      height: '30px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-list-check"></i>
                    </div>
                    Récapitulatif des modifications
                  </h6>
                  
                  <div className="row">
                    <div className="col-md-6">
                      <table className="table table-sm" style={{ color: COLORS.darkText }}>
                        <tbody>
                          <tr>
                            <td width="40%" style={{ fontWeight: '500' }}>Agent :</td>
                            <td>
                              {formData.id_agent ? (
                                (() => {
                                  const agent = agents.find(a => a.id_agent.toString() === formData.id_agent);
                                  return agent ? `${agent.nom_agent} ${agent.prenom_agent}` : 'Agent non trouvé';
                                })()
                              ) : (
                                <span className="text-danger">Non sélectionné</span>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td style={{ fontWeight: '500' }}>Titre :</td>
                            <td>
                              {formData.titre_tache || <span className="text-danger">Non renseigné</span>}
                            </td>
                          </tr>
                          <tr>
                            <td style={{ fontWeight: '500' }}>Description :</td>
                            <td>
                              {formData.description_tache ? (
                                <div title={formData.description_tache}>
                                  {formData.description_tache.length > 50 ? 
                                    `${formData.description_tache.substring(0, 50)}...` : 
                                    formData.description_tache}
                                </div>
                              ) : (
                                <span className="text-danger">Non renseignée</span>
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="col-md-6">
                      <table className="table table-sm" style={{ color: COLORS.darkText }}>
                        <tbody>
                          <tr>
                            <td width="40%" style={{ fontWeight: '500' }}>Date début :</td>
                            <td>
                              {formData.date_debut ? (
                                new Date(formData.date_debut).toLocaleString('fr-FR')
                              ) : (
                                <span className="text-danger">Non renseignée</span>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td style={{ fontWeight: '500' }}>Date fin :</td>
                            <td>
                              {formData.date_fin ? (
                                new Date(formData.date_fin).toLocaleString('fr-FR')
                              ) : (
                                <span className="text-danger">Non renseignée</span>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td style={{ fontWeight: '500' }}>Priorité :</td>
                            <td>
                              {formData.priorite && (
                                <span className="badge" style={{ 
                                  backgroundColor: `${getPriorityColor(formData.priorite)}20`,
                                  color: getPriorityColor(formData.priorite),
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  fontWeight: '500',
                                  border: `1px solid ${getPriorityColor(formData.priorite)}`
                                }}>
                                  {formData.priorite.charAt(0).toUpperCase() + formData.priorite.slice(1)}
                                </span>
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="d-flex justify-content-between mt-4 pt-4 border-top" style={{ borderColor: COLORS.lightBg }}>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn fw-semibold"
                    onClick={handleCancel}
                    disabled={saving}
                    style={{
                      color: COLORS.darkText,
                      borderColor: '#e0e0e0',
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!saving) {
                        e.target.style.backgroundColor = COLORS.lightBg;
                        e.target.style.borderColor = COLORS.primary;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.borderColor = '#e0e0e0';
                    }}
                  >
                    <i className="bi bi-x-circle me-2"></i>
                    Annuler
                  </button>
                  
                  <button
                    type="button"
                    className="btn fw-semibold"
                    onClick={handleReset}
                    disabled={saving}
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!saving) {
                        e.target.style.backgroundColor = COLORS.lightBg;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                    }}
                  >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Réinitialiser
                  </button>
                </div>
                
                <button
                  type="submit"
                  className="btn fw-semibold"
                  disabled={saving || !formData.id_agent}
                  style={{
                    color: 'white',
                    backgroundColor: saving || !formData.id_agent ? '#B0BEC5' : COLORS.primary,
                    borderColor: saving || !formData.id_agent ? '#B0BEC5' : COLORS.primary,
                    borderRadius: '8px',
                    padding: '12px 32px',
                    fontSize: '1rem',
                    transition: 'all 0.3s ease',
                    opacity: saving ? 0.8 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!saving && formData.id_agent) {
                      e.target.style.backgroundColor = COLORS.secondary;
                      e.target.style.borderColor = COLORS.secondary;
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 12px rgba(11, 60, 93, 0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!saving && formData.id_agent) {
                      e.target.style.backgroundColor = COLORS.primary;
                      e.target.style.borderColor = COLORS.primary;
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }
                  }}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Modification en cours...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      Mettre à jour la tâche
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .form-label,
        .alert strong {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .form-control,
        .form-select,
        .form-text,
        .alert,
        .table,
        small {
          font-family: 'Roboto', sans-serif;
        }
        
        .form-control:focus,
        .form-select:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(11, 60, 93, 0.25);
        }
        
        .btn,
        .form-select,
        .input-group-text,
        .form-control {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:disabled,
        .form-select:disabled {
          background-color: ${COLORS.lightBg};
          cursor: not-allowed;
        }
        
        .input-group-text {
          background-color: ${COLORS.lightBg};
          border-color: #dee2e6;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .col-md-4, .col-md-6, .col-md-12 {
            margin-bottom: 1rem;
          }
          
          textarea.form-control {
            min-height: 150px;
          }
        }
      `}</style>
    </Layout>
  );
};

export default TacheEdit;